import {
  CURRENT_APP_RELEASE_ID,
  getReleaseIdFromVersionData,
  type ReleaseVersionData,
} from "@/lib/appRelease";

const VERSION_KEY = "app-version";
const TAB_LOADED_VERSION_KEY = "client-shell-loaded-version";
const DEV_RESET_SESSION_KEY = "client-shell-dev-reset-v1";
const PROD_RECOVERY_SESSION_KEY = "client-shell-prod-recovery";
const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const VERSION_FETCH_TIMEOUT_MS = 5000;

type CleanupStats = {
  cacheCount: number;
  registrationCount: number;
};

type VersionData = ReleaseVersionData;

const isBrowser = typeof window !== "undefined";
const LEGACY_CLIENT_RECOVERY_KEY = "client-recovery-v2";

declare global {
  interface Window {
    __forceClientRecovery?: (reason: string) => void;
  }
}

export const isLocalDevHost = () =>
  isBrowser && ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);

export const getClientErrorMessage = (value: unknown): string => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (value instanceof Error) return value.message;
  try {
    return String(value);
  } catch {
    return "";
  }
};

export const isRecoverableAssetError = (message: string): boolean =>
  message.includes("Failed to fetch dynamically imported module") ||
  message.includes("Importing a module script failed") ||
  message.includes("ChunkLoadError") ||
  message.includes("Loading chunk") ||
  message.includes("[lazyWithGuard] Missing default export") ||
  message.includes("[lazyNamedWithGuard] Missing named export");

export const requestClientRecovery = (reason: string): boolean => {
  if (!isBrowser || isLocalDevHost()) {
    return false;
  }

  sessionStorage.removeItem(LEGACY_CLIENT_RECOVERY_KEY);

  if (typeof window.__forceClientRecovery === "function") {
    window.__forceClientRecovery(reason);
    return true;
  }

  window.location.reload();
  return true;
};

const devLog = (message: string, details?: unknown) => {
  if (!isBrowser || !import.meta.env.DEV) return;
  if (details === undefined) {
    // eslint-disable-next-line no-console
    console.info(`[client-shell] ${message}`);
    return;
  }
  // eslint-disable-next-line no-console
  console.info(`[client-shell] ${message}`, details);
};

export const clearClientCachesAndWorkers = async (): Promise<CleanupStats> => {
  let cacheCount = 0;
  let registrationCount = 0;

  if ("caches" in window) {
    const cacheNames = await caches.keys();
    cacheCount = cacheNames.length;
    await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
  }

  if ("serviceWorker" in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    registrationCount = registrations.length;
    await Promise.all(registrations.map((registration) => registration.unregister()));
  }

  return { cacheCount, registrationCount };
};

const fetchVersionData = async (): Promise<VersionData | null> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), VERSION_FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(`/version.json?t=${Date.now()}`, {
      method: "GET",
      cache: "no-store",
      signal: controller.signal,
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
      },
    });

    if (!response.ok) {
      return null;
    }

    return (await response.json()) as VersionData;
  } catch (error) {
    devLog("version fetch failed", error);
    return null;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const getLoadedVersionForThisTab = (): string =>
  sessionStorage.getItem(TAB_LOADED_VERSION_KEY) ?? CURRENT_APP_RELEASE_ID;

const rememberCurrentReleaseForThisTab = () => {
  sessionStorage.setItem(TAB_LOADED_VERSION_KEY, CURRENT_APP_RELEASE_ID);
};

const rememberLoadedVersionForThisTab = (version: string) => {
  sessionStorage.setItem(TAB_LOADED_VERSION_KEY, version);
  localStorage.setItem(VERSION_KEY, version);
};

const reloadClient = async (reason: string, nextVersion?: string): Promise<false> => {
  const recoveryKey = nextVersion
    ? `${PROD_RECOVERY_SESSION_KEY}:${nextVersion}`
    : DEV_RESET_SESSION_KEY;

  if (sessionStorage.getItem(recoveryKey) === "done") {
    devLog("skipping repeated reload", { reason, nextVersion });
    return true as never;
  }

  sessionStorage.setItem(recoveryKey, "done");

  if (nextVersion) {
    rememberLoadedVersionForThisTab(nextVersion);
  }

  const cleanupStats = await clearClientCachesAndWorkers();
  devLog("client recovery reload", { reason, nextVersion, ...cleanupStats });

  window.location.reload();
  return false;
};

export const initializeClientShell = async (): Promise<boolean> => {
  if (!isBrowser) {
    return true;
  }

  rememberCurrentReleaseForThisTab();

  if (isLocalDevHost() && import.meta.env.DEV) {
    devLog("localhost dev bootstrap — skip cache/service-worker purge");
    return true;
  }

  if (isLocalDevHost()) {
    const cleanupStats = await clearClientCachesAndWorkers();
    devLog("localhost bootstrap cleanup", cleanupStats);
    return true;
  }

  const versionData = await fetchVersionData();
  if (!versionData) {
    return true;
  }

  const remoteReleaseId = getReleaseIdFromVersionData(versionData);
  if (!remoteReleaseId) {
    return true;
  }

  const loadedVersion = getLoadedVersionForThisTab();
  if (loadedVersion !== remoteReleaseId) {
    await reloadClient("production-bootstrap-version-mismatch", remoteReleaseId);
    return false;
  }

  rememberLoadedVersionForThisTab(remoteReleaseId);
  sessionStorage.removeItem(`${PROD_RECOVERY_SESSION_KEY}:${remoteReleaseId}`);
  return true;
};

export const startClientVersionWatcher = () => {
  if (!isBrowser || isLocalDevHost()) {
    return () => {};
  }

  let disposed = false;
  let isChecking = false;
  rememberCurrentReleaseForThisTab();

  const checkVersion = async () => {
    if (disposed || isChecking) {
      return;
    }

    isChecking = true;
    try {
      const versionData = await fetchVersionData();
      if (!versionData) {
        return;
      }

      const remoteReleaseId = getReleaseIdFromVersionData(versionData);
      if (!remoteReleaseId) {
        return;
      }

      const loadedVersion = getLoadedVersionForThisTab();
      if (loadedVersion !== remoteReleaseId) {
        await reloadClient("production-watcher-version-mismatch", remoteReleaseId);
        return;
      }

      rememberLoadedVersionForThisTab(remoteReleaseId);
    } finally {
      isChecking = false;
    }
  };

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== VERSION_KEY || !event.newValue) {
      return;
    }

    const loadedVersion = getLoadedVersionForThisTab();
    if (loadedVersion && loadedVersion !== event.newValue) {
      void reloadClient("production-cross-tab-version-mismatch", event.newValue);
    }
  };

  const intervalId = window.setInterval(() => {
    void checkVersion();
  }, VERSION_CHECK_INTERVAL_MS);

  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      void checkVersion();
    }
  };

  const handlePageShow = (event: PageTransitionEvent) => {
    if (event.persisted) {
      void checkVersion();
    }
  };

  document.addEventListener("visibilitychange", handleVisibilityChange);
  window.addEventListener("pageshow", handlePageShow);
  window.addEventListener("storage", handleStorage);

  return () => {
    disposed = true;
    window.clearInterval(intervalId);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    window.removeEventListener("pageshow", handlePageShow);
    window.removeEventListener("storage", handleStorage);
  };
};
